/**
 * Orders Vuex Module
 *
 * Store хранит raw API data, никаких вычислений.
 * FSM-логика в getters через импортированные функции.
 */

import * as ordersApi from "../api/orders.api.js"
import { payOrder } from "../api/orders.api.js"
import {
  deriveOrderUIState,
  canTriggerEvent,
  getAvailableEvents,
  isOrderActive,
  isOrderTerminal,
} from "../model/order.fsm.js"
import { OrderEvent } from "../model/order.types.js"

// ============================================================================
// STATE
// ============================================================================

const state = () => ({
  /** @type {import('../model/order.types.js').Order[]} */
  orders: [],

  /** @type {import('../model/order.types.js').Order|null} */
  currentOrder: null,

  /** @type {boolean} */
  loading: false,

  /** @type {boolean} */
  listLoading: false,

  /** @type {Error|null} */
  error: null,
})

// ============================================================================
// GETTERS
// ============================================================================

const getters = {
  /**
   * Список заказов
   */
  orders: (state) => state.orders || [],

  /**
   * Текущий заказ
   */
  currentOrder: (state) => state.currentOrder,

  /**
   * Статус загрузки
   */
  isLoading: (state) => state.loading,

  /**
   * Статус загрузки списка
   */
  isListLoading: (state) => state.listLoading,

  /**
   * Ошибка
   */
  error: (state) => state.error,

  /**
   * Тип текущего пользователя (seller/reseller)
   * @returns {string|null}
   */
  userType: (_state, _getters, rootState) => rootState.user?.type || null,

  /**
   * UI состояние заказа (вычисляется FSM)
   * @returns {string|null} OrderUIState или null если нет заказа
   */
  uiState: (state, _getters, rootState) => {
    if (!state.currentOrder) return null
    const userType = rootState.user?.type
    return deriveOrderUIState(state.currentOrder, userType)
  },

  /**
   * Список доступных событий (вычисляется FSM)
   * @returns {string[]} Массив OrderEvent
   */
  availableEvents: (state, _getters, rootState) => {
    if (!state.currentOrder) return []
    const userType = rootState.user?.type
    return getAvailableEvents(state.currentOrder, userType)
  },

  /**
   * Функция для проверки доступности конкретного события
   * @returns {function(string): boolean}
   */
  canTrigger: (state, _getters, rootState) => (event) => {
    if (!state.currentOrder) return false
    const userType = rootState.user?.type
    return canTriggerEvent(state.currentOrder, event, userType)
  },

  /**
   * Активна ли сделка (request.accepted + payment.paid + deal not cancelled)
   */
  isActive: (state) => {
    if (!state.currentOrder) return false
    return isOrderActive(state.currentOrder)
  },

  /**
   * Находится ли заказ в терминальном состоянии
   * ВАЖНО: терминальность — свойство заказа, НЕ зависит от роли
   */
  isTerminal: (state) => {
    if (!state.currentOrder) return false
    return isOrderTerminal(state.currentOrder)
  },

  /**
   * Проверка доступности конкретных действий (shorthand)
   */
  canAcceptRequest: (_state, getters) =>
    getters.canTrigger(OrderEvent.ACCEPT_REQUEST),
  canRejectRequest: (_state, getters) =>
    getters.canTrigger(OrderEvent.REJECT_REQUEST),
  canCancelRequest: (_state, getters) =>
    getters.canTrigger(OrderEvent.CANCEL_REQUEST),
  canPay: (_state, getters) => getters.canTrigger(OrderEvent.PAY),
  canConfirmReady: (_state, getters) =>
    getters.canTrigger(OrderEvent.UPDATE_STATE),
  canConfirmComplete: (_state, getters) =>
    getters.canTrigger(OrderEvent.CONFIRM_COMPLETE),
  canCancelDeal: (_state, getters) =>
    getters.canTrigger(OrderEvent.CANCEL_DEAL),
  canExtendDeadline: (_state, getters) =>
    getters.canTrigger(OrderEvent.EXTEND_DEADLINE),
  canAcceptExtend: (_state, getters) =>
    getters.canTrigger(OrderEvent.ACCEPT_EXTEND),
  canRejectExtend: (_state, getters) =>
    getters.canTrigger(OrderEvent.REJECT_EXTEND),
}

// ============================================================================
// MUTATIONS
// ============================================================================

const mutations = {
  SET_ORDERS(state, orders) {
    state.orders = orders
  },

  APPEND_ORDERS(state, newOrders) {
    const prev = Array.isArray(state.orders) ? state.orders : []
    const next = Array.isArray(newOrders) ? newOrders : []
    if (next.length === 0) {
      state.orders = prev
      return
    }
    const seen = new Set()
    const merged = []
    for (const o of prev) {
      const id = o?.offer?.id ?? o?.id
      if (id != null) seen.add(id)
      merged.push(o)
    }
    for (const o of next) {
      const id = o?.offer?.id ?? o?.id
      if (id != null && seen.has(id)) continue
      if (id != null) seen.add(id)
      merged.push(o)
    }
    state.orders = merged
  },

  SET_ORDER(state, order) {
    state.currentOrder = order
  },

  SET_LOADING(state, loading) {
    state.loading = loading
  },

  SET_LIST_LOADING(state, loading) {
    state.listLoading = loading
  },

  SET_ERROR(state, error) {
    state.error = error
  },

  CLEAR_ORDER(state) {
    state.currentOrder = null
    state.error = null
  },
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * FSM-валидация действия перед выполнением.
 * Делает систему fail-safe: защита от двойного клика, race condition, некорректного вызова.
 *
 * @param {Object} state - Vuex state
 * @param {Object} rootState - Root Vuex state
 * @param {string} event - OrderEvent
 * @throws {Error} Если действие не разрешено FSM
 */
function assertCanTrigger(state, rootState, event) {
  const userType = rootState.user?.type
  if (!canTriggerEvent(state.currentOrder, event, userType)) {
    throw new Error(`FSM: Action "${event}" not allowed in current state`)
  }
}

// ============================================================================
// ACTIONS
// ============================================================================

const actions = {
  /**
   * Загрузить список заказов
   *
   * @param {Object} payload
   * @param {Object} [payload.filters] - UI-фильтры (status, type, date, sort, brand)
   * @param {number} [payload.limit]
   * @param {number} [payload.offset]
   * @param {boolean} [payload.append] - если true, дописать к списку (пагинация)
   */
  async fetchOrders({ commit }, payload = {}) {
    const isPayloadObject =
      payload &&
      typeof payload === "object" &&
      ("filters" in payload ||
        "limit" in payload ||
        "offset" in payload ||
        "append" in payload)

    const filters = isPayloadObject ? payload.filters || {} : payload || {}
    const append = Boolean(isPayloadObject && payload.append)
    const limit =
      (isPayloadObject && payload.limit != null
        ? Number(payload.limit)
        : null) ||
      (filters.limit != null ? Number(filters.limit) : null) ||
      20
    const offset =
      (isPayloadObject && payload.offset != null
        ? Number(payload.offset)
        : null) ||
      (filters.offset != null ? Number(filters.offset) : null) ||
      0

    if (!append) {
      commit("SET_LIST_LOADING", true)
    }
    commit("SET_ERROR", null)

    try {
      // Преобразуем UI-фильтры в API query-параметры
      const params = {}

      // types: offer | deal
      if (filters.type && filters.type !== "all") {
        params.types = filters.type
      }

      // status: pending | approved | declined
      if (filters.status) {
        params.status = filters.status
      }

      // sort: random | date_asc | date_desc
      if (filters.sort) {
        params.sort = filters.sort
      }

      // brand: string
      if (filters.brand) {
        params.brand = filters.brand
      }

      // created_at: datetime в ISO формате (YYYY-MM-DDTHH:MM:SS)
      if (filters.date) {
        if (filters.date instanceof Date) {
          const year = filters.date.getFullYear()
          const month = String(filters.date.getMonth() + 1).padStart(2, "0")
          const day = String(filters.date.getDate()).padStart(2, "0")
          const hours = String(filters.date.getHours()).padStart(2, "0")
          const minutes = String(filters.date.getMinutes()).padStart(2, "0")
          const seconds = String(filters.date.getSeconds()).padStart(2, "0")
          params.created_at = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
        } else {
          // Если передана строка без времени, добавляем T00:00:00
          params.created_at = filters.date.includes("T")
            ? filters.date
            : `${filters.date}T00:00:00`
        }
      }

      params.limit = limit
      params.offset = offset

      console.log("[fetchOrders] filters:", filters, "→ params:", params)
      const result = await ordersApi.getOrders(params)
      // API возвращает { items, total } по контракту
      const rawItems = result.items || result
      const batchSize = Array.isArray(rawItems) ? rawItems.length : 0
      const totalFromApi = result.total != null ? Number(result.total) : null

      let orders = rawItems

      // Клиентская фильтрация по типу (заявка / сделка)
      // offer = заявка (оффер ещё не одобрен), deal = сделка (оффер одобрен)
      if (filters.type === "offer") {
        orders = orders.filter((order) => order.offer?.status !== "approved")
      } else if (filters.type === "deal") {
        orders = orders.filter((order) => order.offer?.status === "approved")
      }

      if (append) {
        commit("APPEND_ORDERS", orders)
      } else {
        commit("SET_ORDERS", orders)
      }

      return {
        orders,
        total: Number.isFinite(totalFromApi) ? totalFromApi : null,
        batchSize,
        rawCount: batchSize,
      }
    } catch (error) {
      commit("SET_ERROR", error)
      console.error("Ошибка при загрузке списка заказов:", error)
      throw error
    } finally {
      if (!append) {
        commit("SET_LIST_LOADING", false)
      }
    }
  },

  /**
   * Загрузить заказ по offer_id (композитная загрузка)
   *
   * Загружает:
   * 1. Offer (по offer_id) — основной источник данных
   * 2. Deal (по offer.deal_id, если есть)
   * 3. Model берётся из offer.items[0].model
   *
   * Fallback: если /seller/deal/ недоступен (reseller),
   * используем /user/offers для получения states
   *
   * Возвращает скомпонованный объект для UI.
   */
  async fetchOrder({ commit }, offerId) {
    commit("SET_LOADING", true)
    commit("SET_ERROR", null)

    try {
      // 1. Загружаем оффер — основной источник данных
      const offer = await ordersApi.getOffer(offerId)

      // 2. Загружаем сделку по deal_id из оффера (если есть)
      let deal = null
      let fallbackOrder = null

      if (offer.deal_id) {
        try {
          deal = await ordersApi.getDeal(offer.deal_id)
        } catch (e) {
          console.warn("Не удалось загрузить сделку через /seller/deal/:", e)
          // Fallback: получаем заказ из /user/offers (работает для reseller)
          try {
            fallbackOrder = await ordersApi.getOrderByOfferId(offerId)
            console.log("Fallback: загружен заказ из /user/offers")
          } catch (fallbackError) {
            console.warn("Fallback также не удался:", fallbackError)
          }
        }
      }

      // 3. Модель берём из offer.items[0].model (уже вложена в ответ)
      const model = offer?.items?.[0]?.model || null

      // 4. Собираем композитный объект для UI
      // Приоритет: deal > fallbackOrder > пустые значения
      const compositeOrder = {
        // ID оффера как основной
        id: offer.id,
        offer_id: offer.id,
        reseller_id: offer.reseller_id,
        seller_id: offer.seller_id,
        status: deal?.status || fallbackOrder?.deal?.status || offer.status,
        created_at: offer.created_at,
        updated_at:
          deal?.updated_at ||
          fallbackOrder?.deal?.updated_at ||
          offer.created_at,
        // Payment URL из сделки (для редиректа на оплату)
        payment_url:
          deal?.payment_url || fallbackOrder?.deal?.payment_url || null,
        // Код получения заказа (приходит на верхнем уровне deal)
        pickup_code: deal?.pickup_code || fallbackOrder?.pickup_code || null,
        // Текущий state (для FSM fallback если states[] пустой)
        state_type: deal?.state_type || fallbackOrder?.state_type || null,
        state_status: deal?.state_status || fallbackOrder?.state_status || null,
        // Флаг завершения торгов (для FSM)
        is_trading_complete:
          deal?.is_trading_complete ??
          fallbackOrder?.is_trading_complete ??
          null,
        // Данные из сделки (если есть)
        reseller: deal?.reseller || fallbackOrder?.reseller || null,
        seller: deal?.seller || fallbackOrder?.seller || null,
        // states: из ответа seller/deal (или fallbackOrder); при type:offer status:expired → CANCELLED_BY_SYSTEM
        states:
          deal?.states ||
          (deal?.data && Array.isArray(deal.data.states) && deal.data.states) ||
          fallbackOrder?.states ||
          offer?.states ||
          [],
        state_history:
          deal?.state_history || fallbackOrder?.state_history || [],
        cancel: deal?.cancel || fallbackOrder?.cancel || null,
        // Вложенные данные
        offer,
        model,
        // Legacy alias для обратной совместимости (FSM использует order.deal.*)
        deal: deal
          ? {
              id: deal.id,
              status: deal.status,
              created_at: deal.created_at,
              updated_at: deal.updated_at,
              payment_url: deal.payment_url || null,
              is_trading_complete: deal.is_trading_complete ?? null,
              delivery_deadline_at: deal.delivery_deadline_at || null,
            }
          : fallbackOrder?.deal
          ? {
              id: fallbackOrder.deal.id,
              status: fallbackOrder.deal.status,
              created_at: fallbackOrder.deal.created_at,
              updated_at: fallbackOrder.deal.updated_at,
              payment_url: fallbackOrder.deal.payment_url || null,
              is_trading_complete:
                fallbackOrder.deal?.is_trading_complete ?? null,
              delivery_deadline_at:
                fallbackOrder.deal?.delivery_deadline_at || null,
            }
          : null,
      }

      commit("SET_ORDER", compositeOrder)
      return compositeOrder
    } catch (error) {
      commit("SET_ERROR", error)
      console.error("Ошибка при загрузке заказа:", error)
      throw error
    } finally {
      commit("SET_LOADING", false)
    }
  },

  /**
   * Принять заявку (seller)
   */
  async acceptRequest({ commit, state, rootState, dispatch }) {
    // FSM-валидация: защита от некорректного вызова
    assertCanTrigger(state, rootState, OrderEvent.ACCEPT_REQUEST)

    const offerId = state.currentOrder?.offer?.id
    if (!offerId) {
      throw new Error("No current order or offer")
    }

    commit("SET_LOADING", true)
    commit("SET_ERROR", null)

    try {
      await ordersApi.acceptRequest(offerId)
      // Перезагружаем полные данные заказа
      await dispatch("fetchOrder", offerId)
      return state.currentOrder
    } catch (error) {
      commit("SET_ERROR", error)
      console.error("Ошибка при принятии заявки:", error)
      throw error
    } finally {
      commit("SET_LOADING", false)
    }
  },

  /**
   * Отклонить заявку (seller)
   * @param {Object} payload - Данные отклонения
   * @param {string} payload.reason - Причина отклонения
   * @param {string} [payload.comment] - Комментарий
   * @param {number} [payload.suggested_price] - Предложенная цена
   */
  async rejectRequest({ commit, state, rootState, dispatch }, payload = {}) {
    assertCanTrigger(state, rootState, OrderEvent.REJECT_REQUEST)

    const offerId = state.currentOrder?.offer?.id
    if (!offerId) {
      throw new Error("No current order or offer")
    }

    commit("SET_LOADING", true)
    commit("SET_ERROR", null)

    try {
      await ordersApi.rejectRequest(offerId, payload)
      // Перезагружаем полные данные заказа
      await dispatch("fetchOrder", offerId)
      return state.currentOrder
    } catch (error) {
      commit("SET_ERROR", error)
      console.error("Ошибка при отклонении заявки:", error)
      throw error
    } finally {
      commit("SET_LOADING", false)
    }
  },

  /**
   * Отменить заявку (reseller)
   */
  async cancelRequest({ commit, state, rootState, dispatch }) {
    assertCanTrigger(state, rootState, OrderEvent.CANCEL_REQUEST)

    const offerId = state.currentOrder?.offer?.id
    if (!offerId) {
      throw new Error("No current order or offer")
    }

    commit("SET_LOADING", true)
    commit("SET_ERROR", null)

    try {
      await ordersApi.cancelRequest(offerId)
      // Перезагружаем полные данные заказа
      await dispatch("fetchOrder", offerId)
      return state.currentOrder
    } catch (error) {
      commit("SET_ERROR", error)
      console.error("Ошибка при отмене заявки:", error)
      throw error
    } finally {
      commit("SET_LOADING", false)
    }
  },

  /**
   * Оплатить заказ (reseller)
   */
  async pay({ commit, state, rootState }) {
    assertCanTrigger(state, rootState, OrderEvent.PAY)

    if (!state.currentOrder?.id) {
      throw new Error("No current order")
    }

    commit("SET_LOADING", true)
    commit("SET_ERROR", null)

    try {
      const updatedOrder = await payOrder(state.currentOrder.id)
      commit("SET_ORDER", updatedOrder)
      return updatedOrder
    } catch (error) {
      commit("SET_ERROR", error)
      console.error("Ошибка при оплате:", error)
      throw error
    } finally {
      commit("SET_LOADING", false)
    }
  },

  /**
   * Подтвердить готовность (seller)
   */
  async confirmSellerReady({ commit, state, rootState, dispatch }) {
    assertCanTrigger(state, rootState, OrderEvent.UPDATE_STATE)

    const dealId = state.currentOrder?.deal?.id
    if (!dealId) {
      throw new Error("No current order or deal")
    }

    commit("SET_LOADING", true)
    commit("SET_ERROR", null)

    try {
      await ordersApi.confirmSellerReady(dealId)
      // Перезагружаем полные данные заказа
      const offerId =
        state.currentOrder?.offer?.id || state.currentOrder?.offer_id
      await dispatch("fetchOrder", offerId)
      return state.currentOrder
    } catch (error) {
      commit("SET_ERROR", error)
      console.error("Ошибка при подтверждении готовности:", error)
      throw error
    } finally {
      commit("SET_LOADING", false)
    }
  },

  /**
   * Подтвердить завершение (reseller)
   */
  async confirmComplete({ commit, state, rootState, dispatch }) {
    assertCanTrigger(state, rootState, OrderEvent.CONFIRM_COMPLETE)

    const dealId = state.currentOrder?.deal?.id
    if (!dealId) {
      throw new Error("No current order or deal")
    }

    commit("SET_LOADING", true)
    commit("SET_ERROR", null)

    try {
      await ordersApi.confirmComplete(dealId)
      // Перезагружаем полные данные заказа
      const offerId =
        state.currentOrder?.offer?.id || state.currentOrder?.offer_id
      await dispatch("fetchOrder", offerId)
      return state.currentOrder
    } catch (error) {
      commit("SET_ERROR", error)
      console.error("Ошибка при подтверждении завершения:", error)
      throw error
    } finally {
      commit("SET_LOADING", false)
    }
  },

  /**
   * Отменить сделку
   *
   * API возвращает DealCancelResponse (не Deal), поэтому после отмены
   * перезагружаем сделку для получения актуального состояния.
   *
   * @param {Object} payload - Данные отмены
   * @param {string} payload.reason - Причина: no_stock | price_not_ok | delivery_too_long | other
   * @param {string|null} [payload.comment] - Комментарий (для reason = "other")
   * @param {number|string|null} [payload.suggestedPrice] - Предложенная цена (для price_not_ok)
   */
  async cancelDeal({ commit, state, rootState, dispatch }, payload = {}) {
    // Убран assertCanTrigger — валидация на бэкенде, чтобы запрос всегда отправлялся
    // и пользователь видел реальную ошибку от сервера

    // ID сделки из deal.id (currentOrder.id теперь offer_id)
    const dealId = state.currentOrder?.deal?.id
    const offerId =
      state.currentOrder?.offer?.id || state.currentOrder?.offer_id
    if (!dealId) {
      throw new Error("No deal found in current order")
    }

    commit("SET_LOADING", true)
    commit("SET_ERROR", null)

    try {
      // 1. Отменить сделку (API возвращает DealCancelResponse)
      // Один endpoint для seller и reseller — бэкенд определяет actor по авторизации
      await ordersApi.cancelDeal(
        dealId,
        payload.reason,
        payload.comment || null,
        payload.suggestedPrice || null
      )

      // 2. Перезагрузить заказ для получения актуального состояния
      await dispatch("fetchOrder", offerId)

      return state.currentOrder
    } catch (error) {
      commit("SET_ERROR", error)
      console.error("Ошибка при отмене сделки:", error)
      throw error
    } finally {
      commit("SET_LOADING", false)
    }
  },

  /**
   * Продлить дедлайн (seller)
   * @param {Object} payload - { dealId, extraHours }
   */
  async extendDeadline({ commit, state, dispatch }, payload) {
    // assertCanTrigger убран — dealId приходит из роута, currentOrder может быть не загружен

    const dealId = payload?.dealId
    const extraHours = payload?.extraHours

    if (!dealId) {
      throw new Error("dealId is required")
    }

    if (!extraHours || typeof extraHours !== "number") {
      throw new Error("extraHours is required and must be a number")
    }

    commit("SET_LOADING", true)
    commit("SET_ERROR", null)

    try {
      await ordersApi.extendDelivery(dealId, extraHours)
      // Перезагружаем полные данные заказа (API возвращает только deal)
      const offerId =
        state.currentOrder?.offer?.id || state.currentOrder?.offer_id
      if (offerId) {
        await dispatch("fetchOrder", offerId)
      }
      return state.currentOrder
    } catch (error) {
      commit("SET_ERROR", error)
      console.error("Ошибка при продлении дедлайна:", error)
      throw error
    } finally {
      commit("SET_LOADING", false)
    }
  },

  /**
   * Принять запрос на продление (reseller)
   */
  async acceptExtend({ commit, state, rootState, dispatch }) {
    assertCanTrigger(state, rootState, OrderEvent.ACCEPT_EXTEND)

    const dealId = state.currentOrder?.deal?.id
    if (!dealId) {
      throw new Error("No current order or deal")
    }

    commit("SET_LOADING", true)
    commit("SET_ERROR", null)

    try {
      await ordersApi.acceptExtend(dealId)
      // Перезагружаем полные данные заказа
      const offerId =
        state.currentOrder?.offer?.id || state.currentOrder?.offer_id
      await dispatch("fetchOrder", offerId)
      return state.currentOrder
    } catch (error) {
      commit("SET_ERROR", error)
      console.error("Ошибка при принятии продления:", error)
      throw error
    } finally {
      commit("SET_LOADING", false)
    }
  },

  /**
   * Отклонить запрос на продление (reseller)
   */
  async rejectExtend({ commit, state, rootState, dispatch }) {
    assertCanTrigger(state, rootState, OrderEvent.REJECT_EXTEND)

    const dealId = state.currentOrder?.deal?.id
    if (!dealId) {
      throw new Error("No current order or deal")
    }

    commit("SET_LOADING", true)
    commit("SET_ERROR", null)

    try {
      await ordersApi.rejectExtend(dealId, "delivery_too_long")
      // Перезагружаем полные данные заказа
      const offerId =
        state.currentOrder?.offer?.id || state.currentOrder?.offer_id
      await dispatch("fetchOrder", offerId)
      return state.currentOrder
    } catch (error) {
      commit("SET_ERROR", error)
      console.error("Ошибка при отклонении продления:", error)
      throw error
    } finally {
      commit("SET_LOADING", false)
    }
  },

  /**
   * Отправить отзыв о сделке
   */
  /**
   * Отправить отзыв о сделке
   *
   * @param {Object} payload
   * @param {number|string} payload.dealId - ID сделки
   * @param {string} payload.comment - Комментарий
   * @param {number|null} [payload.rating] - Оценка (1-5)
   */
  async submitFeedback({ commit }, { dealId, comment, rating = null }) {
    commit("SET_LOADING", true)
    commit("SET_ERROR", null)

    try {
      await ordersApi.submitFeedback(dealId, comment, rating)
    } catch (error) {
      commit("SET_ERROR", error)
      console.error("Ошибка при отправке отзыва:", error)
      throw error
    } finally {
      commit("SET_LOADING", false)
    }
  },

  /**
   * Принять встречное предложение цены (bid) от селлера
   * @param {number|string} dealId - ID сделки
   */
  async acceptBid({ commit, state, dispatch }, dealId) {
    commit("SET_LOADING", true)
    commit("SET_ERROR", null)

    try {
      await ordersApi.acceptBid(dealId)
      // Перезагружаем данные заказа
      const offerId =
        state.currentOrder?.offer?.id || state.currentOrder?.offer_id
      if (offerId) {
        await dispatch("fetchOrder", offerId)
      }
      return state.currentOrder
    } catch (error) {
      commit("SET_ERROR", error)
      console.error("Ошибка при принятии предложения:", error)
      throw error
    } finally {
      commit("SET_LOADING", false)
    }
  },

  /**
   * Отклонить встречное предложение цены (bid) от селлера
   * @param {number|string} dealId - ID сделки
   */
  async rejectBid({ commit, state, dispatch }, dealId) {
    commit("SET_LOADING", true)
    commit("SET_ERROR", null)

    try {
      await ordersApi.rejectBid(dealId)
      // Перезагружаем данные заказа
      const offerId =
        state.currentOrder?.offer?.id || state.currentOrder?.offer_id
      if (offerId) {
        await dispatch("fetchOrder", offerId)
      }
      return state.currentOrder
    } catch (error) {
      commit("SET_ERROR", error)
      console.error("Ошибка при отклонении предложения:", error)
      throw error
    } finally {
      commit("SET_LOADING", false)
    }
  },

  /**
   * Очистить текущий заказ
   */
  clearOrder({ commit }) {
    commit("CLEAR_ORDER")
  },

  /**
   * Сбросить только ошибку (заказ в state сохраняется)
   */
  clearOrderError({ commit }) {
    commit("SET_ERROR", null)
  },
}

// ============================================================================
// EXPORT
// ============================================================================

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
